import { Alert, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, MenuItem, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTrackUsage } from '../../hooks/useTrackUsage';
import PostingService from '../../services/PostingService';
import { taskErrorMessage } from '../../services/TaskTrackingService';
import { platformChoices } from './taskFields';

const INSTAGRAM_FACEBOOK = 'INSTAGRAM_FACEBOOK';

/** Also posting on (rev6 Part 11, 6j-5): a second platform and date, per language. */
const AlsoPostingDialog = ({ task, posting, platforms, onClose, onSaved }) => {
    useTrackUsage('AlsoPostingDialog', 'dialog', 'posts');
    const [languages, setLanguages] = useState(null);
    const [picked, setPicked] = useState([]);
    const [platform, setPlatform] = useState(posting.secondPlatform || '');
    const [date, setDate] = useState(posting.secondDate || '');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let live = true;
        PostingService.panel(task.id)
            .then((v) => {
                if (!live) return;
                const list = v.languages.filter((l) => l.slot === 1);
                setLanguages(list);
                const has = v.languages.filter((l) => l.slot === 2).map((l) => l.languageKey);
                setPicked(has.length ? has : list.map((l) => l.languageKey));
            })
            .catch((e) => live && setError(taskErrorMessage(e)));
        return () => {
            live = false;
        };
    }, [task.id]);

    // English starts unticked for Instagram/Facebook, as it has no English there
    const choosePlatform = (value) => {
        setPlatform(value);
        if (value === INSTAGRAM_FACEBOOK) setPicked((p) => p.filter((k) => k !== 'eng'));
    };
    const toggle = (key, on) => setPicked((p) => (on ? [...p, key] : p.filter((k) => k !== key)));

    const save = async (remove) => {
        setBusy(true);
        setError('');
        try {
            await PostingService.setSecondPosting(task.id, {
                languages: picked,
                platform: remove ? null : platform,
                date: remove ? null : date,
            });
            onSaved();
        } catch (e) {
            setError(taskErrorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Also posting on</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 1.5 }}>Stage due dates stay with the first date.</DialogContentText>
                <TextField select fullWidth margin="dense" label="Platform" value={platform} onChange={(e) => choosePlatform(e.target.value)}>
                    {platformChoices(platforms, platform)
                        .filter((p) => p.value !== task.platform)
                        .map((p) => (
                            <MenuItem key={p.value} value={p.value}>
                                {p.label}
                            </MenuItem>
                        ))}
                </TextField>
                <TextField type="date" fullWidth margin="dense" label="Posting date" InputLabelProps={{ shrink: true }} value={date} onChange={(e) => setDate(e.target.value)} />
                {languages?.map((l) => (
                    <FormControlLabel
                        key={l.languageKey}
                        sx={{ display: 'flex' }}
                        control={<Checkbox checked={picked.includes(l.languageKey)} onChange={(e) => toggle(l.languageKey, e.target.checked)} />}
                        label={<Typography variant="body2">{l.languageLabel}</Typography>}
                    />
                ))}
                {error && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                        {error}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                {posting.secondDate && !posting.secondPosted && (
                    <Button color="inherit" disabled={busy} onClick={() => save(true)} sx={{ mr: 'auto' }}>
                        Remove
                    </Button>
                )}
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" disabled={busy || !platform || !date || picked.length === 0} onClick={() => save(false)}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AlsoPostingDialog;
